import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { MdCategory, MdCreditCard } from "react-icons/md";

const COLORS = [
  "#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#ef4444", "#6366f1",
  "#14b8a6", "#d946ef", "#f97316", "#22c55e", "#eab308",
];

const CostsCharts = ({ costsByService, costsByMeter, error }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const preparePieData = (data) => {
    if (!data || data.length === 0) return [];
    const total = data.reduce((sum, item) => sum + item.totalCost, 0);
    return data.map((item) => ({
      name: item.serviceName || item.meterName,
      value: item.totalCost,
      percentage: (item.totalCost / total) * 100,
    }));
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{data.name}</p>
          <p className="text-primary-cyan text-lg font-bold">
            {formatAmount(data.value)}
          </p>
          <p className="text-gray-400 text-xs">
            Percentage: {data.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const servicePieData = preparePieData(costsByService);
  const meterPieData = preparePieData(costsByMeter);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Costs by Service Pie Chart */}
      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdCategory className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">
            Costs by Service
          </h3>
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-red-400">
              Error loading service costs: {error}
            </p>
          </div>
        ) : servicePieData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No service cost data available</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={servicePieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  animationDuration={1000}
                  animationBegin={0}
                  label={({ name, percent }) =>
                    `${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                >
                  {servicePieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {servicePieData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <span className="text-gray-300 text-xs">{item.name}</span>
                  <span className="text-gray-500 text-xs">
                    ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdCreditCard className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">
            Costs by Meter
          </h3>
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-red-400">
              Error loading meter costs: {error}
            </p>
          </div>
        ) : meterPieData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No meter cost data available</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={meterPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  animationDuration={1000}
                  animationBegin={200}
                  label={({ name, percent }) =>
                    name.length > 20 ? "" : `${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                >
                  {meterPieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 flex flex-wrap justify-center gap-3 max-h-32 overflow-y-auto">
              {meterPieData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <span className="text-gray-300 text-xs truncate max-w-[150px]">
                    {item.name.length > 30
                      ? item.name.substring(0, 27) + "..."
                      : item.name}
                  </span>
                  <span className="text-gray-500 text-xs">
                    ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CostsCharts;